// src/components/layout/VerifyEmailGate.jsx
// Blocks access until email is verified.
// Google OAuth users are always verified — skipped automatically.
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { auth } from "../../config/firebase";
import { MailCheck, RefreshCw } from "lucide-react";

export default function VerifyEmailGate({ children }) {
  const { user, emailVerified, resendVerification } = useAuth();
  const [sent,      setSent]      = useState(false);
  const [sending,   setSending]   = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Google sign-in users have providerData[0].providerId === "google.com"
  // Firebase marks them as emailVerified = true automatically — so this check
  // only triggers for email/password accounts that haven't clicked the link yet.
  if (!user || emailVerified) return children;

  async function handleResend() {
    setSending(true);
    await resendVerification();
    setSending(false);
    setSent(true);
  }

  async function handleRefresh() {
    setRefreshing(true);
    // The cached auth user's emailVerified flag is stale until we explicitly
    // re-fetch it from the server — a plain page reload alone won't pick up
    // a verification done in another tab/email link.
    if (auth.currentUser) {
      try { await auth.currentUser.reload(); } catch { /* ignore */ }
    }
    window.location.reload();
  }

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-surface-100 shadow-sm p-8 max-w-sm w-full text-center">
        <div className="w-14 h-14 rounded-2xl bg-brand-100 flex items-center justify-center mx-auto mb-4">
          <MailCheck size={26} className="text-brand-600" />
        </div>
        <h2 className="font-semibold text-xl text-ink-900 mb-2">Verify your email</h2>
        <p className="text-ink-400 text-sm font-sans mb-1 leading-relaxed">
          We sent a verification link to
        </p>
        <p className="font-mono text-sm text-ink-700 mb-6 break-all">{user.email}</p>
        <p className="text-ink-400 text-xs font-sans mb-6">
          Click the link in that email then refresh this page.
        </p>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-primary w-full mb-3 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={14} /> {refreshing ? "Checking…" : "I've verified — refresh"}
        </button>
        {sent ? (
          <p className="text-green-600 text-xs font-sans">Email sent! Check your inbox.</p>
        ) : (
          <button
            onClick={handleResend}
            disabled={sending}
            className="text-sm text-brand-600 hover:underline font-sans disabled:opacity-50"
          >
            {sending ? "Sending…" : "Resend verification email"}
          </button>
        )}
      </div>
    </div>
  );
}
